import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageSquare, ArrowLeft, Calendar, Search } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const { userTheme } = useTheme();
  
  // Get theme values
  const pageStyles = userTheme?.page || {};
  const announcementsBox = userTheme?.announcementsBox || {};
  const buttonStyles = userTheme?.buttons || {};
  
  // Fallback colors
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  const linkColor = announcementsBox.linkColor || primaryColor;
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterOrder, setFilterOrder] = useState('newest');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [announcementsRes, categoriesRes] = await Promise.all([
        api.get('/announcements'),
        api.get('/announcement-categories')
      ]);
      setAnnouncements(announcementsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const filteredAnnouncements = announcements
    .filter(ann => {
      if (filterCategory && ann.category_id !== parseInt(filterCategory)) return false;
      if (filterSearch) {
        const search = filterSearch.toLowerCase();
        if (!ann.title.toLowerCase().includes(search) && !ann.content.toLowerCase().includes(search)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (filterOrder === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (filterOrder === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  // If viewing single announcement
  if (id) {
    const announcement = announcements.find(a => a.id === parseInt(id));
    
    if (loading) {
      return (
        <Sidebar>
          <div 
            className="min-h-screen flex items-center justify-center"
            style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
          >
            <div 
              className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
              style={{ borderTopColor: primaryColor }}
            ></div>
          </div>
        </Sidebar>
      );
    }

    if (!announcement) {
      return (
        <Sidebar>
          <div 
            className="p-8 min-h-screen"
            style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
          >
            <button
              onClick={() => navigate('/admin/announcements')}
              className="flex items-center gap-2 mb-6 hover:opacity-80"
              style={{ color: linkColor }}
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Announcements
            </button>
            <div 
              className="rounded-xl p-8 text-center"
              style={{ 
                backgroundColor: announcementsBox.backgroundColor || '#FFFFFF',
                border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`
              }}
            >
              <p style={{ color: announcementsBox.textColor || '#6B7280' }}>Announcement not found</p>
            </div>
          </div>
        </Sidebar>
      );
    }

    return (
      <Sidebar>
        <div 
          className="p-8 min-h-screen"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <button
            onClick={() => navigate('/admin/announcements')}
            className="flex items-center gap-2 mb-6 font-medium hover:opacity-80"
            style={{ color: linkColor }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Announcements
          </button>
          
          <div 
            className="rounded-xl overflow-hidden"
            style={{ 
              backgroundColor: announcementsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`
            }}
          >
            <div className="px-6 py-4" style={{ backgroundColor: primaryColor }}>
              <h1 className="text-2xl font-bold text-white">{announcement.title}</h1>
              <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(announcement.created_at).toLocaleDateString()}
                </span>
                {announcement.category_name && (
                  <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                    {announcement.category_name}
                  </span>
                )}
              </div>
            </div>
            <div className="p-6">
              <div 
                className="prose max-w-none"
                style={{ color: announcementsBox.textColor || '#374151' }}
                dangerouslySetInnerHTML={{ __html: announcement.content.replace(/\n/g, '<br/>') }}
              />
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  // List view
  if (loading) {
    return (
      <Sidebar>
        <div 
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div 
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl" style={{ backgroundColor: primaryColor }}>
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 
                className="text-2xl font-semibold"
                style={{ color: pageStyles.titleColor || '#111827' }}
              >
                Announcements
              </h1>
              <p style={{ color: pageStyles.textColor || '#6B7280' }} className="text-sm">
                Stay updated with the latest news
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div 
          className="flex flex-wrap gap-4 mb-6 p-4 rounded-xl"
          style={{ 
            backgroundColor: announcementsBox.backgroundColor || '#FFFFFF',
            border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`
          }}
        >
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:ring-2"
            style={{ 
              border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`,
              color: announcementsBox.textColor || '#374151'
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          <select
            value={filterOrder}
            onChange={(e) => setFilterOrder(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:ring-2"
            style={{ 
              border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`,
              color: announcementsBox.textColor || '#374151'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
          
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: announcementsBox.textColor || '#9CA3AF' }} />
              <input
                type="text"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search announcements..."
                className="w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:ring-2"
                style={{ 
                  border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`,
                  color: announcementsBox.textColor || '#374151'
                }}
              />
            </div>
          </div>
        </div>

        {/* Announcements List */}
        {filteredAnnouncements.length === 0 ? (
          <div 
            className="rounded-xl p-12 text-center"
            style={{ 
              backgroundColor: announcementsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`
            }}
          >
            <MessageSquare className="w-16 h-16 mx-auto mb-4" style={{ color: announcementsBox.borderColor || '#D1D5DB' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: announcementsBox.titleColor || '#4B5563' }}>
              No announcements found
            </h3>
            <p style={{ color: announcementsBox.textColor || '#6B7280' }}>
              Try adjusting your filters or check back soon.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAnnouncements.map((announcement) => (
              <Link
                key={announcement.id}
                to={`/admin/announcements/${announcement.id}`}
                className="block rounded-xl overflow-hidden transition-all hover:shadow-md"
                style={{ 
                  backgroundColor: announcementsBox.backgroundColor || '#FFFFFF',
                  border: `1px solid ${announcementsBox.borderColor || '#E5E7EB'}`
                }}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 
                        className="text-lg font-semibold mb-2 hover:opacity-80"
                        style={{ color: announcementsBox.titleColor || '#111827' }}
                      >
                        {announcement.title}
                      </h3>
                      <p 
                        className="line-clamp-2 text-sm"
                        style={{ color: announcementsBox.textColor || '#6B7280' }}
                      >
                        {announcement.content.substring(0, 150)}...
                      </p>
                    </div>
                    {announcement.category_name && (
                      <span 
                        className="px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
                        style={{ 
                          backgroundColor: `${primaryColor}15`,
                          color: primaryColor
                        }}
                      >
                        {announcement.category_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3 text-xs" style={{ color: announcementsBox.textColor || '#9CA3AF' }}>
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(announcement.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AnnouncementsPage;
